import { ObservableQueryFields } from '@apollo/react-common';
import { NetworkStatus } from 'apollo-client';
import classNames from 'classnames';
import React from 'react';

import Button, { ButtonSizes, ButtonStyles, ButtonTypes } from '@Components/Buttons/Button';
import LoadingSpinner from '@Components/LoadingSpinner';
import Typography from '@Components/Typography/Typography';
import { Messages } from '@Config/messages';
import { PageInfo } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { fetchPaginatedItems } from '@Utils/graphql';
import { MessageKeysWithoutParams } from '@Utils/translation';

import styles from './PaginatedDataBlock.module.scss';

export interface PaginatedDataBlockProps<T, S> {
  loading: boolean;
  dataLength?: number;
  pageInfo?: PageInfo;
  fetchMore: ObservableQueryFields<T, S>['fetchMore'];
  fetchMoreKeyword: string;
  networkStatus: NetworkStatus;
  noRecordsMessage?: MessageKeysWithoutParams;
  noResults?: React.FunctionComponentElement<{}> | null;
  loadItemsFromNewest?: boolean;
}

function PaginatedDataBlock<T, S>(props: PaginatedDataBlockProps<T, S> & { children?: React.ReactNode }) {
  const {
    fetchMore,
    fetchMoreKeyword,
    pageInfo,
    dataLength = 0,
    loading,
    children,
    networkStatus,
    noRecordsMessage = Messages.labelNoResults,
    noResults,
    loadItemsFromNewest,
  } = props;

  // If we are reloading data - refetching or changing variables, then we need to display spinner and no data
  const isReloadingData = [NetworkStatus.setVariables, NetworkStatus.refetch].includes(networkStatus);
  const noDataFound = !loading && !dataLength;

  const loadMore = () => {
    if (pageInfo) {
      const cursor = loadItemsFromNewest ? pageInfo.startCursor : pageInfo.endCursor;
      fetchPaginatedItems(cursor, fetchMore, fetchMoreKeyword, loadItemsFromNewest);
    }
  };

  const t = useTranslations();

  const renderNoResults = () => {
    if ((noDataFound && noResults) || noResults === null) {
      return noResults;
    }
    if (noDataFound && !noResults) {
      return <Typography tag="span" size="l" color="gray" msg={t(noRecordsMessage)} />;
    }
    return undefined;
  };

  const buttonElement = !loading && (
    <div className={classNames(styles.loadMoreSection, styles.bottomSpacing)}>
      <Button
        loading={loading}
        style={ButtonStyles.regular}
        label={t(Messages.btnFetchMore)}
        size={ButtonSizes.fill}
        type={ButtonTypes.submit}
        onClick={loadMore}
      />
    </div>
  );

  return (
    <>
      {loadItemsFromNewest && pageInfo?.hasPreviousPage && buttonElement}
      {loadItemsFromNewest && loading && (
        <div className={classNames(styles.loadingBlock, styles.bottomSpacing)}>
          <LoadingSpinner />
        </div>
      )}
      <div>
        {renderNoResults()}
        {!isReloadingData && !!dataLength && children}
      </div>
      {!loadItemsFromNewest && loading && (
        <div className={styles.loadingBlock}>
          <LoadingSpinner />
        </div>
      )}
      {!loadItemsFromNewest && pageInfo?.hasNextPage && buttonElement}
    </>
  );
}

export default PaginatedDataBlock;
